import React, { useContext, useState, useEffect } from 'react';
import { useUserAllSubscription, useUserHasSubscription } from 'apollo/user';
import { SamplesPlans } from 'types/enums';
import { useAuthentication } from 'components/AuthenticationProvider/AuthenticationProvider';

interface UserSubscriptionProviderProps {
    children: React.ReactNode;
}

export type UserSubscriptionContextType = {
    hasStudioPlan: boolean;
    hasSamplesPlan: boolean;
    hasFetchedSubscription: boolean;
    hasSubscription: boolean;
};

export const defaultUserSubscriptionContext = {
    hasStudioPlan: false,
    hasSamplesPlan: false,
    hasFetchedSubscription: false,
    hasSubscription: false,
};

export const UserSubscriptionContext =
    React.createContext<UserSubscriptionContextType>(
        defaultUserSubscriptionContext,
    );

export const UserSubscriptionProvider = ({
    children,
}: UserSubscriptionProviderProps): JSX.Element => {
    const { hasSubscription } = useUserHasSubscription();
    const { isGuestUser } = useAuthentication();
    const { allSubscription } = useUserAllSubscription();
    const [hasStudioPlan, setHasStudioPlan] = useState(false);
    const [hasSamplesPlan, setHasSamplesPlan] = useState(false);

    // To prevent race condition, always check hasFetchedSubscription first
    // We do not fetch subscription for guest user.
    const hasFetchedSubscription = isGuestUser || !!allSubscription;

    useEffect(() => {
        const hasStudioPlan = allSubscription?.some((subscription) => {
            const isLandrStudioPlan =
                subscription?.plan.code ===
                    SamplesPlans.all_access_annual_billed_monthly ||
                subscription?.plan.code === SamplesPlans.all_access ||
                subscription?.plan.code === SamplesPlans.studio_pro;

            return isLandrStudioPlan;
        });

        setHasStudioPlan(hasStudioPlan);
    }, [allSubscription]);

    useEffect(() => {
        const hasSamplesPlan = allSubscription?.some((subscription) => {
            const isLandrSamplesPlan =
                subscription?.plan.code === SamplesPlans.samples_tier1 ||
                subscription?.plan.code === SamplesPlans.samples_tier2 ||
                subscription?.plan.code === SamplesPlans.samples_tier3 ||
                subscription?.plan.code === SamplesPlans.samples_pro;

            return isLandrSamplesPlan;
        });

        setHasSamplesPlan(hasSamplesPlan);
    }, [allSubscription]);

    return (
        <UserSubscriptionContext.Provider
            value={{
                hasStudioPlan,
                hasSamplesPlan,
                hasSubscription,
                hasFetchedSubscription,
            }}
        >
            {children}
        </UserSubscriptionContext.Provider>
    );
};

export const useUserSubscriptionContext = (): UserSubscriptionContextType =>
    useContext(UserSubscriptionContext);

export default UserSubscriptionProvider;
